import React, { useState } from "react";
import { Elements, ExpressCheckoutElement, useElements } from "@stripe/react-stripe-js";
import { useTranslation } from "react-i18next";

// Apple Pay and Google Pay only; the other one-click methods stay off.
export const EXPRESS_CHECKOUT_OPTIONS = {
  buttonType: { applePay: "book", googlePay: "book" },
  paymentMethods: {
    applePay: "auto",
    googlePay: "auto",
    link: "never",
    paypal: "never",
    amazonPay: "never",
    klarna: "never",
  },
  layout: { overflow: "never" },
};

// Elements for the wallet total. The server charges the booking, so the
// payment method is created here, which Stripe allows only in manual mode.
export const walletElementsOptions = (amount, currency, locale) => ({
  mode: "payment",
  amount,
  currency,
  locale,
  paymentMethodCreation: "manual",
});

const WalletButtons = ({ stripe, billingDetails, validate, pay, onError, onAvailable }) => {
  const elements = useElements();

  // The payment sheet opens only once the contact details and terms are in.
  const handleClick = (event) => {
    if (validate()) {
      event.resolve();
    }
  };

  // Whether the booking is paid; Stripe's errors go to the guest.
  const payWithWallet = async () => {
    const { error: submitError } = await elements.submit();
    if (submitError) {
      onError(submitError.message);
      return false;
    }

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      elements,
      params: { billing_details: billingDetails },
    });
    if (error) {
      onError(error.message);
      return false;
    }

    return pay(paymentMethod.id);
  };

  // Every failure closes the payment sheet, so it never waits for a timeout.
  const handleConfirm = async (event) => {
    let paid = false;
    try {
      paid = await payWithWallet();
    } catch (error) {
      onError(error?.message);
    }
    if (!paid) {
      event.paymentFailed({ reason: "fail" });
    }
  };

  return (
    <ExpressCheckoutElement
      options={EXPRESS_CHECKOUT_OPTIONS}
      onReady={(event) =>
        onAvailable(Object.values(event?.availablePaymentMethods || {}).some(Boolean))
      }
      onClick={handleClick}
      onConfirm={handleConfirm}
    />
  );
};

// Apple Pay and Google Pay buttons for the total from the server, in cents.
// They show only where the browser offers a wallet, above the card form.
const WalletPayment = ({ stripe, amount, currency, locale, ...buttonProps }) => {
  const { t } = useTranslation();
  const [available, setAvailable] = useState(false);

  if (!stripe || !amount) {
    return null;
  }

  return (
    <div className={`wallet-payment ${available ? "" : "wallet-payment--pending"}`}>
      <Elements stripe={stripe} options={walletElementsOptions(amount, currency, locale)}>
        <WalletButtons stripe={stripe} onAvailable={setAvailable} {...buttonProps} />
      </Elements>
      {available && (
        <p className='payment-divider'>
          <span>{t("pay_by_card_divider", "or pay by card")}</span>
        </p>
      )}
    </div>
  );
};

export default WalletPayment;
